import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, List, RefreshCw, Key, ShieldCheck, HelpCircle, Lock, Zap, UserCheck, Settings, ShieldAlert, EyeOff, AlertTriangle, Fingerprint, Database } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Como Alterar Dados de Login IPTV", href: "/blog/como-alterar-dados-login-iptv" }
  ];

  const faq = [
    {
      question: "Com que frequência devo trocar minha senha?",
      answer: "Recomendamos a troca a cada 90 dias por segurança, ou imediatamente se você compartilhou seus dados com terceiros ou utilizou redes Wi-Fi públicas."
    },
    {
      question: "A troca de senha é imediata?",
      answer: "Sim. Assim que a alteração é processada no servidor da P2 PRO, o login antigo é invalidado em tempo real em todos os dispositivos."
    },
    {
      question: "Posso escolher minha própria senha?",
      answer: "Em planos Master, sim. Para assinantes individuais, as senhas são geradas aleatoriamente pelo sistema para garantir o máximo de segurança contra ataques de força bruta."
    },
    {
      question: "O que acontece se eu esquecer a nova senha?",
      answer: "Você deve entrar em contato com o suporte ou acessar o painel do cliente para solicitar um reset de senha. O processo é validado via e-mail ou WhatsApp cadastrado."
    }
  ];

  const benefits = [
    {
      title: "Controle de Acesso",
      description: "Garanta que apenas você e as pessoas autorizadas utilizem sua assinatura.",
      icon: <Lock className="w-6 h-6" />
    },
    {
      title: "Integridade de Dados",
      description: "Proteja seus favoritos e histórico de serem alterados por invasores.",
      icon: <ShieldCheck className="w-6 h-6" />
    },
    {
      title: "Privacidade de Uso",
      description: "Evite que terceiros monitorem seus hábitos de consumo de mídia.",
      icon: <EyeOff className="w-6 h-6" />
    }
  ];

  const howItWorks = [
    {
      title: "Protocolo de Identidade",
      description: "Valide sua identidade através do e-mail ou telefone de cadastro."
    },
    {
      title: "Geração de Credencial",
      description: "Receba o novo Usuário ou Senha através de canal seguro."
    },
    {
      title: "Logout Sistêmico",
      description: "Realize o logout manual em todos os dispositivos ativos no momento."
    },
    {
      title: "Re-Autenticação",
      description: "Insira os novos dados e valide a conexão com o servidor."
    }
  ];

  const diferenciais = [
    {
      title: "Criptografia de Ponta",
      description: "Nossas senhas são armazenadas em hashes de alta segurança (SHA-256).",
      icon: <Fingerprint className="w-6 h-6" />
    },
    {
      title: "Audit Log",
      description: "Mantenha um registro de todos os IPs que acessaram sua conta recentemente.",
      icon: <Database className="w-6 h-6" />
    }
  ];

  const sections = [
    {
      title: "1. A Importância Crítica da Segurança Cibernética no IPTV",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            No cenário digital de 2026, a segurança dos seus dados de acesso ao IPTV não é apenas uma questão de conveniência, mas de proteção patrimonial digital. Seus dados de login são chaves de entrada para um servidor de alta performance. O vazamento dessas informações pode resultar em quedas de sinal, lentidão por uso indevido e até o bloqueio preventivo da sua conta pelo sistema de anti-fraude da <strong>P2 PRO</strong>.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Alterar seus dados regularmente é a melhor prática para manter sua 'higiene digital'. Neste guia denso, explicamos como gerenciar suas credenciais de forma profissional e o que fazer em casos de suspeita de comprometimento.
          </p>
        </div>
      )
    },
    {
      title: "2. Quando a Alteração de Dados torna-se Obrigatória",
      content: (
        <div className="prose prose-invert max-w-none space-y-6">
          <p className="text-muted-foreground">Existem gatilhos críticos que exigem a troca imediata das suas informações de login:</p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-red-500/5 rounded-2xl border border-red-500/20">
              <h4 className="font-bold text-red-400 mb-3 flex items-center gap-2"><ShieldAlert className="w-5 h-5" /> Uso Não Autorizado</h4>
              <p className="text-sm text-muted-foreground">Se você notar que canais estão sendo trocados sozinhos ou se receber avisos de 'Excesso de conexões', sua senha foi comprometida.</p>
            </div>
            <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
              <h4 className="font-bold text-white mb-3 flex items-center gap-2"><Zap className="w-5 h-5 text-electric" /> Fim de Compartilhamento</h4>
              <p className="text-sm text-muted-foreground">Se você emprestou sua conta temporariamente para um amigo ou familiar, altere a senha logo após o uso para retomar o controle total.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. Guia Técnico: Como Alterar os Dados no Aplicativo",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric flex items-center justify-center flex-shrink-0 text-white font-black italic">STEP 1</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Solicitação de Reset</h4>
              <p className="text-muted-foreground">Acesse a Central do Cliente P2 PRO ou entre em contato com seu revendedor Master. Por questões de segurança, a senha de streaming é gerada no lado do servidor para garantir que atenda aos critérios de complexidade.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric flex items-center justify-center flex-shrink-0 text-white font-black italic">STEP 2</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Processo de Logout Global</h4>
              <p className="text-muted-foreground">Antes de inserir os novos dados, você deve garantir que o aplicativo antigo foi desconectado. Vá em <em>Settings {'>'} Profile {'>'} Logout</em>. Em alguns casos, é necessário limpar o cache do aplicativo nas configurações do sistema (Android/Smart TV).</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric flex items-center justify-center flex-shrink-0 text-white font-black italic">STEP 3</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Inserção de Novas Credenciais</h4>
              <p className="text-muted-foreground">Utilize o novo Usuário e Senha. Lembre-se que a URL do servidor (Host) geralmente permanece a mesma, a menos que tenhamos realizado uma migração de infraestrutura.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Dicas de Ouro para a Nova Senha",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Para assinantes que podem customizar suas senhas, siga este padrão de segurança militar:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Mínimo de 12 Caracteres</h5>
                <p className="text-sm text-muted-foreground">Senhas curtas podem ser quebradas em minutos por softwares de força bruta.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Mistura de Caracteres</h5>
                <p className="text-sm text-muted-foreground">Use letras maiúsculas, minúsculas, números e ao menos um símbolo especial (ex: @, #, !).</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-500 mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Evite o Óbvio</h5>
                <p className="text-sm text-muted-foreground">Nunca use datas de nascimento, sequências numéricas (123456) ou o nome do serviço (p2pro123).</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Central de Ajuda IPTV"
        title="Como Alterar Dados de Login IPTV: Guia de Segurança Máxima"
        subtitle="Proteja sua assinatura contra invasores e garanta o controle total sobre seus acessos e preferências."
        heroImage="https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "A segurança da sua conta é a nossa prioridade número um. Em um mundo cada vez mais conectado, seus dados de streaming são ativos valiosos que precisam ser protegidos com o mesmo rigor que uma conta bancária digital.",
          "Neste guia denso, detalhamos não apenas o 'como', mas o 'porquê' da gestão de credenciais. Explicamos os riscos de senhas fracas, o processo técnico de renovação de chaves e como garantir que apenas dispositivos autorizados utilizem seu sinal.",
          "Siga nosso protocolo de segurança P2 PRO e durma tranquilo sabendo que sua diversão está blindada contra qualquer tentativa de uso indevido."
        ]}
        breadcrumbs={breadcrumbs}
        faq={faq}
        benefits={benefits}
        howItWorks={howItWorks}
        diferenciais={diferenciais}
        sections={sections}
        ctaTitle="🔐 Suspeita de Uso Indevido?"
        ctaDescription="Se você acha que alguém está usando sua conta sem permissão, aja rápido. Solicite o bloqueio preventivo agora."
        ctaButtonText="Bloquear e Resetar Senha"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/como-alterar-dados-login-iptv")({
  head: () => ({
    meta: [
      { title: "Como Alterar Dados de Login IPTV - Guia de Segurança Denso" },
      { name: "description", content: "Aprenda a gerenciar suas senhas de IPTV de forma profissional. Dicas de segurança, protocolos de reset e proteção contra invasões para assinantes P2 PRO." },
    ],
  }),
  component: ArticlePage,
});
